import { useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { pick, useContent } from "@/lib/content";

const Field = ({ testid, placeholder, options, value, onChange }) => (
  <div className="flex-1 border-b border-hairline px-1 pb-1 md:border-b-0 md:border-r md:px-6 md:pb-0">
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className="h-14 w-full border-0 bg-transparent px-1 font-body text-[13px] uppercase tracking-[0.14em] text-ink shadow-none focus:ring-0 md:h-16 data-[placeholder]:text-stone/60"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="border-hairline bg-bone">
        {options.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            data-testid={`${testid}-option-${o.value}`}
            className="font-body text-sm uppercase tracking-wide"
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/**
 * The hero's selection bar. The game list is no longer hard-coded copy — it is
 * whatever the estate has published, so the bar can never offer a hunt that is
 * not on the price list. Submitting jumps to the rates, anchored on the pick.
 */
export const SelectionBar = () => {
  const { t, lang } = useLang();
  const s = t.selection;
  const { data } = useContent();

  const [region, setRegion] = useState("");
  const [game, setGame] = useState("");
  const [season, setSeason] = useState("");

  const gameOptions = useMemo(
    () => (data?.species ?? []).map((sp) => ({
      value: sp.slug,
      label: pick(sp.names, lang),
    })),
    [data, lang],
  );

  const asOptions = (arr) => arr.map((o) => ({ value: o, label: o }));

  const onSubmit = () => {
    // Land on the chosen species when there is one, otherwise on the rates.
    const target = game
      ? document.querySelector(`[data-testid="species-card-${game}"]`)
      : document.getElementById("tarife");
    const el = target || document.getElementById("tarife");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="relative z-30 mx-auto -mt-20 max-w-[1360px] px-5 md:-mt-16 md:px-10">
      <Reveal
        y={60}
        className="overflow-hidden border border-hairline bg-bone shadow-[0_30px_80px_-40px_rgba(26,26,24,0.5)]"
        data-testid="selection-bar"
      >
        <div className="flex items-center justify-between border-b border-hairline px-6 py-4 md:px-8">
          <span className="font-display text-xl font-medium uppercase tracking-[0.1em] text-ink md:text-2xl">
            {s.label}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-olive">
            {String(gameOptions.length).padStart(2, "0")} / {data?.quotas?.length ?? 24}
          </span>
        </div>

        <div className="flex flex-col md:flex-row md:items-stretch">
          <div className="flex flex-1 flex-col md:flex-row">
            <Field
              testid="select-region"
              placeholder={s.fields.region.placeholder}
              options={asOptions(s.fields.region.options)}
              value={region}
              onChange={setRegion}
            />
            <Field
              testid="select-game"
              placeholder={s.fields.game.placeholder}
              options={gameOptions.length ? gameOptions : asOptions(s.fields.game.options)}
              value={game}
              onChange={setGame}
            />
            <Field
              testid="select-season"
              placeholder={s.fields.season.placeholder}
              options={asOptions(s.fields.season.options)}
              value={season}
              onChange={setSeason}
            />
          </div>
          <button
            data-testid="selection-submit"
            onClick={onSubmit}
            className="group flex items-center justify-center gap-3 bg-ink px-10 py-5 font-body text-[13px] font-semibold uppercase tracking-[0.18em] text-bone transition-colors duration-500 hover:bg-olive md:py-0"
          >
            {s.cta}
            <span className="transition-transform duration-500 group-hover:translate-x-1">
              →
            </span>
          </button>
        </div>
      </Reveal>
    </section>
  );
};
